import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Loader2, Search, Send, Trash2, Eye, Download, BarChart3,
  Star, Archive, ArchiveRestore, Settings, Copy, Globe, Ban,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  getProposals, deleteProposal, downloadProposalPdf, getProposalLabels,
  createProposalLabel, setProposalFavourite, setProposalArchived, getProposalTemplates,
  duplicateProposal, setProposalPublic, setProposalLabels,
} from "../api/prospectingReportsApi";
import CreateProposalModal from "../components/CreateProposalModal";
import ReadReceiptBadge from "../components/ReadReceiptBadge";
import SendProposalModal from "../components/SendProposalModal";
import ProposalWorkspaceNav from "../components/ProposalWorkspaceNav";
import NotificationSettingsModal from "../components/NotificationSettingsModal";
import NotificationFeedModal from "../components/NotificationFeedModal";
import ManageTeammatesModal from "../components/ManageTeammatesModal";
import ProposalSettingsModal from "../components/ProposalSettingsModal";

const TEAL = "#0f8d9b";
const STATUS_STYLES = {
  draft: "bg-[#f2f3f5] text-[#68727d]",
  sent: "bg-[#eaf1f3] text-[#0f8d9b]",
  viewed: "bg-[#fff4e5] text-[#c6811f]",
  accepted: "bg-[#e9f7ee] text-[#2f9e6b]",
  declined: "bg-[#fdeaea] text-[#c0392b]",
};
const TABS = [
  ["all", "All"], ["draft", "Draft"], ["live", "Live"],
  ["opened", "Opened"], ["accepted", "Accepted"], ["callback", "Callback"], ["favourites", "Favourites"],
];

const auDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};

const ProposalsListPage = () => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [counts, setCounts] = useState({});
  const [labels, setLabels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("current");
  const [tab, setTab] = useState("all");
  const [activeLabel, setActiveLabel] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [sendFor, setSendFor] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeed, setShowFeed] = useState(false);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [teammatesFor, setTeammatesFor] = useState(null);
  const [labelPickerFor, setLabelPickerFor] = useState(null);

  const toggleLabel = async (p, label) => {
    const current = (p.labels || []).map((l) => l.id);
    const ids = current.includes(label.id) ? current.filter((i) => i !== label.id) : [...current, label.id];
    try {
      const { data } = await setProposalLabels(p.id, ids);
      patchRow(p.id, { labels: data.labels });
    } catch (e) { toast.error("Could not update labels."); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { view, tab };
      if (search.trim()) params.q = search.trim();
      if (activeLabel) params.label = activeLabel.id;
      const { data } = await getProposals(params);
      setProposals(data?.results || []);
      setCounts(data?.counts || {});
    } catch (e) {
      toast.error("Could not load your proposals.");
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, [search, view, tab, activeLabel]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    getProposalLabels().then(({ data }) => setLabels(data?.results || [])).catch(() => {});
    getProposalTemplates({ scope: "system" }).then(({ data }) => setTemplates((data?.results || []).slice(0, 8))).catch(() => {});
  }, []);

  const patchRow = (id, changes) => setProposals((prev) => prev.map((x) => (x.id === id ? { ...x, ...changes } : x)));

  const toggleFav = async (p) => {
    patchRow(p.id, { is_favourite: !p.is_favourite });
    try { await setProposalFavourite(p.id, !p.is_favourite); } catch (e) { patchRow(p.id, { is_favourite: p.is_favourite }); toast.error("Could not update."); }
  };
  const toggleArchive = async (p) => {
    try {
      await setProposalArchived(p.id, !p.is_archived);
      setProposals((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(p.is_archived ? "Restored." : "Archived.");
      load();
    } catch (e) { toast.error("Could not update."); }
  };
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete the proposal for ${p.full_address}?`)) return;
    try { await deleteProposal(p.id); setProposals((prev) => prev.filter((x) => x.id !== p.id)); toast.success("Deleted."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete it."); }
  };
  const handlePdf = async (p) => {
    try {
      const { data } = await downloadProposalPdf(p.id);
      const url = URL.createObjectURL(data); window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Could not build the PDF."); }
  };
  const openPreview = (p) => {
    const url = p.share_url || (p.share_token && `${window.location.origin}/${p.share_token}`);
    if (url) window.open(url, "_blank", "noopener");
  };
  const handleDuplicate = async (p) => {
    try { const { data } = await duplicateProposal(p.id); toast.success("Duplicated."); navigate(`/prospecting/proposals/${data.id}`); }
    catch (e) { toast.error("Could not duplicate it."); }
  };
  const togglePublic = async (p) => {
    const next = !p.is_public;
    try {
      const { data } = await setProposalPublic(p.id, next);
      patchRow(p.id, { is_public: data.is_public, is_offline: data.is_offline, status: data.status });
      toast.success(data.is_public ? "This proposal is live." : "Taken offline.");
    } catch (e) { toast.error("Could not update."); }
  };

  const addLabel = async () => {
    const name = window.prompt("New label name:");
    if (!name) return;
    try { const { data } = await createProposalLabel({ name }); setLabels((prev) => [...prev, data]); }
    catch (e) { toast.error("Could not add the label."); }
  };

  return (
    <div className="p-4 md:p-6 flex gap-6">
      <ProposalWorkspaceNav
        active="proposals" view={view}
        onView={(v) => { setView(v); setActiveLabel(null); setTab("all"); }}
        labels={labels} activeLabel={activeLabel}
        onLabel={(l) => { setActiveLabel(l); setView("current"); }}
        onAddLabel={addLabel} onOpenNotifications={() => setShowFeed(true)}
      />

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {view === "archived" ? "Archived proposals" : activeLabel ? activeLabel.name : "Current proposals"}
            <button onClick={() => setShowSettings(true)} className="text-gray-300 hover:text-[#0f8d9b]" title="Proposal settings">
              <Settings className="w-5 h-5" />
            </button>
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/prospecting/proposals/report")} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"><BarChart3 className="w-4 h-4" /> Report</button>
            <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-90" style={{ backgroundColor: TEAL }}><Plus className="w-4 h-4" /> New proposal</button>
          </div>
        </div>

        {/* Filter tabs with counts */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative mr-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search address or vendor"
              className="pl-9 pr-3 py-1.5 w-56 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40" />
          </div>
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setActiveLabel(null); }}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold ${tab === key && !activeLabel ? "bg-[#eaf7f8] text-[#0f8d9b]" : "text-gray-500 hover:bg-gray-50"}`}>
              {label} ({counts[key] ?? 0})
            </button>
          ))}
        </div>

        {/* Create from a template strip */}
        {view === "current" && !activeLabel && templates.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-bold text-gray-700">Create from a template</h2>
              <button onClick={() => setShowCreate(true)} className="text-sm font-semibold text-[#0f8d9b]">Browse all</button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {templates.map((t) => (
                <button key={t.id} onClick={() => setShowCreate(true)}
                  className="shrink-0 w-44 h-24 rounded-xl overflow-hidden border border-gray-200 bg-cover bg-center flex items-end hover:border-[#0f8d9b]"
                  style={t.cover_image_url ? { backgroundImage: `url(${t.cover_image_url})` } : { background: `linear-gradient(135deg, ${t.theme_colour || "#1f2a44"}, #0b1220)` }}>
                  <span className="text-white text-xs font-semibold px-2 py-1.5 drop-shadow">{t.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : proposals.length === 0 ? (
          <div className="border border-dashed border-gray-200 rounded-xl py-16 text-center text-sm text-gray-500">
            {view === "archived" ? "No archived proposals." : "No proposals here yet."}
          </div>
        ) : (
          <div className="space-y-3">
            {proposals.map((p) => (
              <div key={p.id} className="bg-white border border-gray-100 rounded-xl p-4 hover:border-gray-200">
                <div className="flex items-start gap-4">
                  <button onClick={() => navigate(`/prospecting/proposals/${p.id}`)} className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLES[p.status] || ""}`}>{p.status_label || p.status}</span>
                      {p.is_public
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#e9f7ee] text-[#2f9e6b]"><span className="w-1.5 h-1.5 rounded-full bg-[#2f9e6b]" /> Live</span>
                        : p.status !== "draft" ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-200 text-gray-600">Offline</span> : null}
                      <ReadReceiptBadge proposal={p} />
                      <span className="text-xs text-gray-400">Created {auDate(p.created_at)}</span>
                    </div>
                    <p className="text-base font-bold text-gray-900 truncate">{p.full_address || p.title || "Untitled proposal"}</p>
                    <p className="text-xs text-gray-500">Owner: {p.agent?.name || "—"}{p.recipient_name ? ` · ${p.recipient_name}` : ""}</p>
                    {(p.labels || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {p.labels.map((l) => (
                          <span key={l.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${l.colour}1a`, color: l.colour }}>{l.name}</span>
                        ))}
                      </div>
                    )}
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-center">
                      <div className="text-xl font-extrabold text-gray-900 leading-none">{p.view_count || 0}</div>
                      <div className="text-[10px] text-gray-400">views</div>
                    </div>
                    <button onClick={() => toggleFav(p)} className="p-2 rounded-lg hover:bg-gray-50" title="Favourite">
                      <Star className={`w-4 h-4 ${p.is_favourite ? "fill-[#f5b301] text-[#f5b301]" : "text-gray-400"}`} />
                    </button>
                    <button onClick={() => openPreview(p)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-500" title="Preview"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => navigate(`/prospecting/proposals/${p.id}/analytics`)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-500" title="Analytics"><BarChart3 className="w-4 h-4" /></button>
                    <button onClick={() => handlePdf(p)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-500" title="PDF"><Download className="w-4 h-4" /></button>
                    <button onClick={() => handleDuplicate(p)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-500" title="Duplicate"><Copy className="w-4 h-4" /></button>
                    <button onClick={() => togglePublic(p)} className="p-2 rounded-lg hover:bg-gray-50" title={p.is_public ? "Take offline" : "Make live (publish)"}>
                      {p.is_public ? <Globe className="w-4 h-4 text-[#2f9e6b]" /> : <Ban className="w-4 h-4 text-gray-400" />}
                    </button>
                    {p.status === "draft" && <button onClick={() => setSendFor(p)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-500" title="Send"><Send className="w-4 h-4" /></button>}
                    <button onClick={() => toggleArchive(p)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-500" title={p.is_archived ? "Restore" : "Archive"}>
                      {p.is_archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleDelete(p)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-50 flex items-center gap-1.5 text-xs text-gray-500 relative">
                  <span>Shared with:</span>
                  <button onClick={() => setTeammatesFor(p)} className="font-semibold text-[#0f8d9b] hover:underline">
                    {p.shared_with_team
                      ? "Entire team"
                      : (p.collaborators && p.collaborators.length)
                        ? `${p.collaborators.length} teammate${p.collaborators.length > 1 ? "s" : ""}`
                        : "Add collaborator"}
                  </button>
                  <span className="mx-1 text-gray-300">·</span>
                  <span>Labels:</span>
                  <button onClick={() => setLabelPickerFor(labelPickerFor === p.id ? null : p.id)} className="font-semibold text-[#0f8d9b] hover:underline">
                    {(p.labels || []).length ? "Edit labels" : "Add label"}
                  </button>
                  {labelPickerFor === p.id && (
                    <div className="absolute z-20 top-full left-0 mt-1 w-60 rounded-lg border border-gray-100 bg-white shadow-lg py-1">
                      {labels.length === 0 && <p className="px-3 py-2 text-gray-400">No labels yet — add one in the sidebar.</p>}
                      {labels.map((l) => {
                        const on = (p.labels || []).some((x) => x.id === l.id);
                        return (
                          <button key={l.id} onClick={() => toggleLabel(p, l)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50">
                            <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-[#0f8d9b] border-[#0f8d9b]" : "border-gray-300"}`}>
                              {on && <span className="text-white text-[10px] leading-none">✓</span>}
                            </span>
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: l.colour || "#6b7280" }} />
                            <span className="text-gray-700 truncate">{l.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && <CreateProposalModal onClose={() => setShowCreate(false)} onCreated={(p) => { setShowCreate(false); navigate(`/prospecting/proposals/${p.id}`); }} />}
      {sendFor && <SendProposalModal proposal={sendFor} onClose={() => setSendFor(null)} onSent={(u) => { patchRow(u.id, u); setSendFor(null); }} />}
      {showSettings && <ProposalSettingsModal onClose={() => setShowSettings(false)} />}
      {showFeed && (
        <NotificationFeedModal
          onClose={() => setShowFeed(false)}
          onOpenSettings={() => { setShowFeed(false); setShowNotifPrefs(true); }}
          onOpenProposal={(id) => { setShowFeed(false); navigate(`/prospecting/proposals/${id}`); }}
        />
      )}
      {showNotifPrefs && <NotificationSettingsModal onClose={() => setShowNotifPrefs(false)} />}
      {teammatesFor && (
        <ManageTeammatesModal proposal={teammatesFor} onClose={() => setTeammatesFor(null)}
          onSaved={(patch) => patchRow(teammatesFor.id, patch)} />
      )}
    </div>
  );
};

export default ProposalsListPage;
